// src/lib/axios.ts
import axios, {
  AxiosError,
  isAxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { z } from "zod";
import { auth } from "./auth";
import { resolveOrgSlug } from "./organization";

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

const NON_ORG_PREFIXES = ["/api/schema", "/api/docs", "/api/redoc"];

function injectOrgSlug(url: string | undefined): string | undefined {
  if (!url) return url;
  if (!url.startsWith("/api/")) return url;

  for (const prefix of NON_ORG_PREFIXES) {
    if (url.startsWith(prefix)) return url;
  }

  const orgSlug = resolveOrgSlug();
  // Every org-scoped route needs the slug; an unprefixed /api/... URL matches
  // no backend route (or the nginx HTML fallback). Fail loudly instead.
  if (!orgSlug) {
    throw new AxiosError(
      "Organization not resolved: set VITE_ORG_SLUG or serve the app from the organization's subdomain.",
      "ERR_ORG_NOT_RESOLVED",
    );
  }

  // Check if the URL already has the org slug injected
  const targetPrefix = `/api/${orgSlug}/`;
  if (url.startsWith(targetPrefix)) {
    return url; // Skip injection, it's already there!
  }

  return `/api/${orgSlug}/${url.slice("/api/".length)}`;
}

// nginx serves index.html with a 200 for any /api/ path that misses a proxy
// location. Without this check that HTML lands in `response.data`, every
// `data.slug` / `data.length` read yields undefined, and callers loop forever
// because nothing ever looks like an error. Treat it as what it is: the request
// never reached the API. Checked on the header, not the body, so blob downloads
// (xlsx/PDF exports) are covered too.
function assertJsonResponse(response: AxiosResponse): AxiosResponse {
  const contentType = response.headers?.["content-type"];
  if (typeof contentType === "string" && contentType.includes("text/html")) {
    throw new AxiosError(
      "API request was not routed to the backend (received an HTML page instead of data). Check the nginx /api/ proxy configuration.",
      "ERR_BAD_RESPONSE",
      response.config,
      response.request,
      response,
    );
  }
  return response;
}

// A 401 the refresh cookie couldn't cure means the session is over. Clear the
// token *first* — otherwise every in-flight request 401s again, re-assigns
// location.href, restarts the navigation, and the app never actually reaches
// /logout. The flag collapses concurrent 401s into a single navigation.
//
// This also covers 401 `token_stale` (access token issued before a password
// change elsewhere) and `session_revoked` (logged out in another tab): the
// server has already refused the refresh cookie, so logging out is all there
// is to do.
let redirecting = false;

function handleUnauthorized() {
  if (redirecting) return;
  redirecting = true;
  auth.resetToken();
  // `expired`: the server already refused this session, so /logout must not
  // call auth/logout/ -- the cookie jar may by now hold a sibling tab's
  // freshly rotated cookie, and revoking it would log that tab out too (T-234).
  window.location.href = "/logout?expired=true";
}

// The refresh token rides in an HttpOnly cookie scoped to /api/<org>/auth/.
// `withCredentials` lets the browser send it in dev, where the API is another
// origin; X-Requested-With is the backend's CSRF guard for these endpoints.
const sessionInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

sessionInstance.interceptors.request.use((config) => {
  config.url = injectOrgSlug(config.url);
  return config;
});
sessionInstance.interceptors.response.use(assertJsonResponse);

const refreshResponseSchema = z.object({ token: z.string() });

/**
 * What a refresh attempt established (T-234):
 * - `ok`: a new access token.
 * - `ended`: the server refused the refresh cookie (HTTP 401) -- the session
 *   is over and the user has to log in again.
 * - `unavailable`: no verdict -- network error, timeout, 429, 5xx, or an HTML
 *   page from a proxy mid-deploy. The session may be perfectly alive, so this
 *   must never be treated as a logout.
 */
export type RefreshResult =
  | { kind: "ok"; token: string }
  | { kind: "ended" }
  | { kind: "unavailable"; status?: number; retryAfterMs?: number };

/** `AxiosError.code` of a request that got a 401 and whose refresh got no
 * verdict (T-245). The session is untouched; the request simply failed. */
export const SESSION_UNAVAILABLE_CODE = "ERR_SESSION_UNAVAILABLE";

export const SESSION_UNAVAILABLE_MESSAGE =
  "Couldn't reach the server — your change was not saved. Try again.";

export type SessionUnavailableError = AxiosError & {
  /** HTTP status the refresh got (429, 5xx), absent for a network error. */
  refreshStatus?: number;
  /** The refresh's Retry-After, when the server sent one. */
  retryAfterMs?: number;
};

export function isSessionUnavailable(
  error: unknown,
): error is SessionUnavailableError {
  return isAxiosError(error) && error.code === SESSION_UNAVAILABLE_CODE;
}

function retryAfterMs(error: unknown): number | undefined {
  if (!isAxiosError(error)) return undefined;
  const raw: unknown = error.response?.headers?.["retry-after"];
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : undefined;
}

// Rejected instead of the original 401 so the global handlers can tell "no
// verdict on the session" from "session over" -- the 401 body would otherwise
// read as an auth failure and be dropped without a word.
function sessionUnavailableError(
  original: AxiosError,
  result: Extract<RefreshResult, { kind: "unavailable" }>,
): SessionUnavailableError {
  const error: SessionUnavailableError = new AxiosError(
    SESSION_UNAVAILABLE_MESSAGE,
    SESSION_UNAVAILABLE_CODE,
    original.config,
    original.request,
  );
  error.refreshStatus = result.status;
  error.retryAfterMs = result.retryAfterMs;
  return error;
}

const SIBLING_ROTATION_RETRY_MS = 300;

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function requestAccessToken(): Promise<RefreshResult> {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await sessionInstance.post("/api/auth/refresh/");
      const { token } = refreshResponseSchema.parse(response.data);
      auth.setToken(token);
      return { kind: "ok", token };
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        // Without Web Locks another tab can rotate the shared cookie between
        // our read and the server's check, so ours arrives already
        // blacklisted while the jar now holds its successor. One short retry
        // presents that successor. "no_session" means there is no cookie at
        // all -- nothing to retry.
        const code = (error.response.data as { code?: string } | undefined)
          ?.code;
        if (attempt === 0 && code !== "no_session") {
          await delay(SIBLING_ROTATION_RETRY_MS);
          continue;
        }
        auth.resetToken();
        return { kind: "ended" };
      }
      return {
        kind: "unavailable",
        status: isAxiosError(error) ? error.response?.status : undefined,
        retryAfterMs: retryAfterMs(error),
      };
    }
  }
}

let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Trade the refresh cookie for a new access token. Concurrent callers in this
 * tab share one request, and tabs queue behind a Web Lock: every refresh
 * rotates the (shared) cookie and blacklists the old one, so two tabs
 * refreshing at once would otherwise present a dead cookie.
 */
export function refreshSession(): Promise<RefreshResult> {
  if (!refreshInFlight) {
    const run = () => requestAccessToken();
    const locked =
      typeof navigator !== "undefined" && navigator.locks
        ? navigator.locks.request("toms-session-refresh", run)
        : run();
    refreshInFlight = locked.finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** The new access token, or `null` when none could be obtained (session over
 * or server unreachable -- use `refreshSession()` to tell them apart). */
export async function refreshAccessToken(): Promise<string | null> {
  const result = await refreshSession();
  return result.kind === "ok" ? result.token : null;
}

/** Server side of logout: blacklist the cookie's session and clear it.
 * Best-effort — logout is local either way. */
export function endServerSession(): Promise<void> {
  return sessionInstance
    .post("/api/auth/logout/")
    .then(() => undefined)
    .catch(() => undefined);
}

const retriedRequests = new WeakSet<InternalAxiosRequestConfig>();

/** On a 401, refresh once and replay the request with the new token.
 * Resolves to the replayed response; `"ended"` when the server refused the
 * session; the refresh's `unavailable` result when it got no verdict (the
 * caller should fail this request, not log out). */
async function retryWithFreshToken(
  client: AxiosInstance,
  error: AxiosError,
): Promise<
  AxiosResponse | "ended" | Extract<RefreshResult, { kind: "unavailable" }>
> {
  const original = error.config;
  // Already replayed once and still 401: the fresh token was refused too.
  if (!original || retriedRequests.has(original)) return "ended";
  retriedRequests.add(original);
  const result = await refreshSession();
  if (result.kind === "ended") return "ended";
  if (result.kind === "unavailable") return result;
  original.headers.Authorization = `Bearer ${result.token}`;
  return client(original);
}

/** What the response interceptors do with `retryWithFreshToken`'s outcome. */
async function handle401(client: AxiosInstance, error: AxiosError) {
  const replayed = await retryWithFreshToken(client, error);
  if (replayed === "ended") {
    handleUnauthorized();
    return Promise.reject(error);
  }
  if ("kind" in replayed) {
    // No verdict: fail this request with a distinguishable error; the
    // session is left alone (T-245).
    return Promise.reject(sessionUnavailableError(error, replayed));
  }
  return replayed;
}

// Main instance with interceptors
const instance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: {
    indexes: null,
  },
});

instance.interceptors.request.use(
  (config) => {
    config.url = injectOrgSlug(config.url);
    const token = auth.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/** `FeatureGatePermission`'s 404 body — a module the org hasn't enabled or
 * the caller's role blocks. Distinct from an ordinary missing record. */
const FEATURE_GATE_DETAIL = "Feature not available for this organization.";

let featureGateHandler: (() => void) | null = null;

/** Registered by the feature store; kept as a callback so this module doesn't
 * import the store that imports it. */
export function setFeatureGateHandler(handler: () => void) {
  featureGateHandler = handler;
}

instance.interceptors.response.use(
  assertJsonResponse,
  async (error) => {
    if (error.response?.status === 401) return handle401(instance, error);
    if (
      error.response?.status === 404 &&
      error.response?.data?.detail === FEATURE_GATE_DETAIL
    ) {
      featureGateHandler?.();
    }
    return Promise.reject(error);
  }
);

export const authInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  // Login's response sets the refresh cookie; cross-origin (dev) the browser
  // only stores it for a credentialed request.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

authInstance.interceptors.request.use(
  (config) => {
    config.url = injectOrgSlug(config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

authInstance.interceptors.response.use(
  assertJsonResponse,
  async (error) => {
    // `authInstance` also carries the anonymous flows (login, forgot/reset
    // password), where a 401 is a normal answer — a wrong password must not
    // bounce anyone to /logout. Only a 401 held *while a token exists* means an
    // expired access token worth refreshing.
    if (error.response?.status === 401 && auth.getToken()) {
      return handle401(authInstance, error);
    }
    return Promise.reject(error);
  }
);

export default instance;
